import { useNavigate } from 'react-router-dom'
import { Plus, ArrowRight, Lock, LogIn } from 'lucide-react'
import type { Party, CanCreatePartyResult } from '@/types'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface JoinNationCTAProps {
  nationId: string
  maxParties: number
  activePartyCount: number
  myParty: Party | null | undefined
  canCreate: CanCreatePartyResult | undefined
  isLoading: boolean
  isLoggedIn: boolean
}

export function JoinNationCTA({
  nationId,
  maxParties,
  activePartyCount,
  myParty,
  canCreate,
  isLoading,
  isLoggedIn,
}: JoinNationCTAProps) {
  const navigate = useNavigate()

  // Loading state
  if (isLoading) {
    return (
      <Card className="mb-8 border-dashed">
        <CardContent className="flex items-center justify-center py-8">
          <div className="bg-muted h-10 w-48 animate-pulse rounded" />
        </CardContent>
      </Card>
    )
  }

  // Not logged in
  if (!isLoggedIn) {
    return (
      <Card className="mb-8 border-blue-200 bg-blue-50/50">
        <CardContent className="py-6">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Join the Political Arena</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                Log in to create your own political party and compete in elections.
              </p>
            </div>
            <Button onClick={() => navigate('/login')}>
              <LogIn className="mr-2 h-4 w-4" />
              Log In to Join
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // User already has a party in this nation
  if (myParty) {
    return (
      <Card className="mb-8 border-green-200 bg-green-50/50">
        <CardContent className="py-6">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
            <div
              className="h-8 w-8 shrink-0 rounded-full"
              style={{ backgroundColor: myParty.color }}
            />
            <div className="flex-1">
              <h3 className="text-lg font-semibold">You're already in the game!</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                You lead <strong>{myParty.name}</strong> in this nation.
              </p>
            </div>
            <Button onClick={() => navigate(`/parties/${myParty.id}`)}>
              <ArrowRight className="mr-2 h-4 w-4" />
              Go to Party Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Nation is full
  if (activePartyCount >= maxParties) {
    return (
      <Card className="mb-8 border-yellow-200 bg-yellow-50/50">
        <CardContent className="py-6">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
            <div className="bg-yellow-100 flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
              <Lock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Nation at Capacity</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                This nation has reached its maximum of {maxParties} parties. Check back later
                or explore other nations.
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/join')}>
              Browse Other Nations
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Can't create for other reason
  if (canCreate && !canCreate.allowed) {
    return (
      <Card className="mb-8 border-yellow-200 bg-yellow-50/50">
        <CardContent className="py-6">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
            <div className="bg-yellow-100 flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
              <Lock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Unable to Join</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                {canCreate.reason || 'You cannot create a party in this nation at this time.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Can create a party - show prominent CTA
  return (
    <Card className="mb-8 border-primary/20 bg-primary/5">
      <CardContent className="py-6">
        <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
          <div className="bg-primary/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
            <Plus className="text-primary h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">Ready to Make Your Mark?</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              Create your political party and start building a movement. Define your ideology,
              recruit candidates, and campaign for power.
            </p>
          </div>
          <Button size="lg" onClick={() => navigate(`/nations/${nationId}/parties/new`)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Your Party
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
